import { Button } from "components/ui/Button";
import { createIcon } from "components/icons/IconFactory";
import { FilterIcon, SearchIcon } from "components/icons/Icons";
import { Sizes } from "enums/sizes";
import { useDialog } from "hooks/useDialog";
import { Download, Plus } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import TableFilters from "./TableFilters";
import { TableConfig } from "./types";

interface TableHeaderProps<T extends Record<string, any>> {
  title?: {
    label: string,
    iconKey?: string,
    description?: string
  };
  actions?: TableConfig<T>['actions'];
  filters?: TableConfig<T>['filters'];
  toolbarExtra?: React.ReactNode;
  onFilter?: (columnKey: string, value: string) => void
  selectedFiltersByGroup?: Record<string, string[]>
}

const TableHeader = <T extends Record<string, any>>({ title, actions, filters, toolbarExtra, onFilter, selectedFiltersByGroup }: TableHeaderProps<T>) => {
  const { t } = useTranslation()
  const { openDialog } = useDialog()
  const renderIcon = useMemo(() => {
    if (title?.iconKey) {
      const Icon = createIcon(title.iconKey)
      return <Icon className="text-primary w-7" />
    }
    return <></>
  }, [title?.iconKey])

  const openFilter = useCallback(() => {
    // Panneau à facettes (nouveau système) : le bouton délègue au parent
    if (filters?.onFilterClick) {
      filters.onFilterClick();
      return;
    }
    if (filters?.referential && filters.referential.length > 0)
      openDialog({
        title: 'Filtrer par',
        size: Sizes["2xl"],
      }, <TableFilters referential={filters.referential} onFilter={onFilter} selectedFiltersByGroup={selectedFiltersByGroup} />)
  }, [openDialog, filters, onFilter, selectedFiltersByGroup])

  return (
    <div>
      {title && (
        <div className="mb-5">
          <h2 className="flex items-center gap-3 text-3xl font-semibold text-zinc-800">
            {renderIcon}
            {title.label}
          </h2>
          {title.description && (
            <p className="mt-1 text-sm text-gray-500">{title.description}</p>
          )}
        </div>
      )}
      {/* Barre d'outils collée au tableau : recherche visible + export + filtres */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {filters?.enabled && filters?.searchable && (
          <div className="relative w-full max-w-sm">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <SearchIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
            </div>
            <input
              type="text"
              placeholder={filters.searchPlaceholder || "Rechercher..."}
              value={filters.searchString}
              onChange={(e) => filters.onSearch && filters.onSearch(e.target.value)}
              className="h-10 w-full rounded-xl border border-gray-300 bg-white pl-9 pr-3 text-sm leading-5 text-gray-900 placeholder-gray-500 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-25"
            />
          </div>
        )}
        {toolbarExtra}
        {actions?.export && (
          <Button
            outline
            onClick={actions.export.onClick}
            disabled={actions.export.loading}
          >
            <Download data-slot="icon" />
            {actions.export.label || 'Exporter'}
          </Button>
        )}
        {filters?.enabled && (
          <Button
            color="primary"
            onClick={openFilter}
          >
            <FilterIcon className="text-primary" />
            {t('COMMON.FILTER')}
            {!!filters?.activeFiltersCount && (
              <span className="ml-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-white/25 px-1 text-xs font-semibold text-white">
                {filters.activeFiltersCount}
              </span>
            )}
          </Button>
        )}
        {actions?.create && (
          <Button
            round
            color={actions.create.color ?? 'secondary'}
            onClick={actions.create.onClick}
          >
            <Plus size={16} />
            {actions.create.label}
          </Button>
        )}
      </div>
    </div>
  );
};

export default TableHeader
